// React
import { useEffect, useMemo, useRef, useState } from "react";

// Third-Party
import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

// Internal
import { useNavigatingAway } from "@hooks/useNavigatingAway";
import { getManualDocumentJson } from "@pages/DocumentsV2/Drafts/draftsService";
import { getCreateDocumentRoute } from "@pages/ManualDocumentV2/utils/getTaxRatesRoute";
import { handleDocumentCreateError } from "@pages/ManualDocumentV2/utils/handleDocumentCreateError";
import { PATH_PAYABLES, PATH_RECEIVABLES } from "@routes/paths";
import { GetAsync, PostFileAsync } from "src/common/httpRequests";
import { API_GATEWAY } from "src/constants/settings";
import useLocales from "src/hooks/useLocales";
import useDocumentNumber from "src/pages/ManualDocumentV2/hooks/useDocumentNumber";
import { useLegalRepresentativeValidation } from "src/pages/ManualDocumentV2/hooks/useLegalRepresentativeValidation";
import useTaxRates from "src/pages/ManualDocumentV2/hooks/useTaxRates";
import { buildDefaults } from "./buildDefaults";
import { buildPayload } from "./buildPayload";
import {
  defaultCustomerProfileMapper,
  defaultDocumentMapper,
  defaultVendorProfileMapper,
} from "./defaultMappers";

// Types
import { ILegalRepStatus } from "../../../../@types/legalrepresentative";
import type { IFormConfig, IFormContext, IFormEngine } from "../types";

interface IUseFormEngineArgs {
  config: IFormConfig;
  refreshPage: number;
}

export const useFormEngine = ({
  config,
  refreshPage,
}: IUseFormEngineArgs): IFormEngine => {
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("id") ?? "";
  const draftDocId = searchParams.get("draftDocId") ?? "";
  const isDuplicate = Boolean(searchParams.get("isDuplicate") ?? "");
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false);
  const [formResetSignal, setFormResetSignal] = useState(0);
  const [fileName, setFileName] = useState("");
  const pdfBlobRef = useRef<Blob | null>(null);
  const [isDocSeriesDialogVisible, setIsDocSeriesDialogVisible] =
    useState(false);
  const [duplicateDocumentNumber, setDuplicateDocumentNumber] = useState("");

  const [peppolOptions, setPeppolOptions] = useState<any[]>([]);
  const [customerPeppolOptions, setCustomerPeppolOptions] = useState<any[]>([]);
  const [companiesList, setCompaniesList] = useState<any[]>([]);
  const [companyLegalRep, setCompanyLegalRep] = useState<any>(null);

  const [legalRepStatus, setLegalRepStatus] = useState<ILegalRepStatus>("idle");

  const formContext: IFormContext = {
    searchParams,
    invoiceType: searchParams.get("invoiceType") ?? "regular",
    fileId,
    isDuplicate,
  };

  const defaults = buildDefaults(config);
  const schema = config.schema(translate, formContext);

  const resolveEndpoint = (ep: IFormConfig["createEndpoint"], values: any) =>
    typeof ep === "function" ? ep(formContext, values) : ep;

  const methods = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: defaults,
    shouldUnregister: false,
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = methods;

  const customerId = watch("customerId");
  const vendorCompanyID = watch("vendorCompanyID");

  const actualCompanyId = useMemo(() => {
    if (!vendorCompanyID || !companiesList.length) return null;
    const selectedCompany = companiesList.find(
      (c: any) =>
        c.registrationId === vendorCompanyID ||
        c.registrationId2 === vendorCompanyID ||
        c.registrationId3 === vendorCompanyID,
    );
    return selectedCompany ? selectedCompany.id : null;
  }, [vendorCompanyID, companiesList]);

  const items = useFieldArray({ name: "items", control });
  const taxes = useFieldArray({ name: "taxes", control });

  const {
    handleFetchDocumentNumber,
    isLoading,
    error: docNumberError,
  } = useDocumentNumber({
    documentClass: config.documentClass,
    setValue,
    invoiceType: config.invoiceType,
  });

  const [isInitLoading, setIsInitLoading] = useState(true);

  const { taxRates, handleFetchTaxRates } = useTaxRates(
    config.taxRatesChannel ?? "",
  );

  const isSDI =
    config.taxRatesChannel === "SDI" || config.complianceStandardKey === "sdi";
  const requiresLegalValidation = config.requiresLegalRep && !isSDI;

  const legalRep = useLegalRepresentativeValidation(
    requiresLegalValidation ? companyLegalRep : null,
    docNumberError,
  );

  const validationError =
    requiresLegalValidation && vendorCompanyID && legalRepStatus === "invalid"
      ? legalRep.error
      : null;

  const isLegalRepValid =
    requiresLegalValidation && vendorCompanyID
      ? legalRepStatus === "valid"
      : true;

  const handleRedirectToDocumentScreen = (isSaveAsDraft: boolean) => {
    const { documentClass, accountClass } = config;

    const route =
      accountClass === "AccountPayable"
        ? PATH_PAYABLES.general.purchaseOrder.root
        : documentClass === "DeliveryNote"
        ? PATH_RECEIVABLES.general.saleDeliveryNote.root
        : PATH_RECEIVABLES.general.saleInvoice.root;

    navigate(`${route}${isSaveAsDraft ? "/drafts" : ""}`);
  };

  const message = translate(
    "You have unsaved changes. Are you sure you want to leave?",
  );
  const [isNavigationTriggered, setIsNavigationTriggered] = useState(false);

  const [showDialogLeavingPage, confirmNavigation, cancelNavigation] =
    useNavigatingAway(isNavigationTriggered ? false : isDirty ?? false);

  const pickProfileMapper = () =>
    config.mapUserProfile ??
    (config.accountClass === "AccountReceivable"
      ? defaultVendorProfileMapper
      : defaultCustomerProfileMapper);

  const fetchUserProfile = async (): Promise<Record<string, any>> => {
    const res = await GetAsync(`${API_GATEWAY}/api/userservice/Profile`);
    if (res.status !== 200) return {};

    const mapped = pickProfileMapper()(res.data);
    let profileValues = { ...defaults, ...mapped };

    if (config.defaultFormValuesKey) {
      const defRes = await GetAsync(
        `${API_GATEWAY}/api/manualdocumentservice/ManualDocument/GetDefaultFormValues`,
      );
      if (defRes.status === 200) {
        const match = defRes.data?.formFields?.find(
          (f: any) => f.formName === config.defaultFormValuesKey,
        );
        if (match?.fields) {
          profileValues = { ...profileValues, ...match.fields };
        }
      }
    }

    return profileValues;
  };

  const fetchDocument = async (profileValues: Record<string, any>) => {
    const res = await GetAsync(
      `${API_GATEWAY}/api/documentservice/DocumentReview/${fileId}`,
    );
    if (res.status !== 200) {
      reset(profileValues);
      handleFetchDocumentNumber();
      handleFetchTaxRates();
      return;
    }

    const data = res.data?.postProcessedJson?.Document;
    const mapper = config.mapDocumentFetch ?? defaultDocumentMapper;
    const mapped = mapper(data, { isDuplicate, taxRates });
    setFileName(data.documentName);
    reset({ ...profileValues, referenceDocumentId: fileId, ...mapped });
    handleFetchDocumentNumber();
    handleFetchTaxRates();
  };

  const fetchCompaniesList = async () => {
    if (requiresLegalValidation) {
      try {
        const res = await GetAsync(
          `${API_GATEWAY}/api/userservice/Company/list`,
        );
        if (res.status === 200 && res.data) {
          setCompaniesList(res.data.companies || res.data);
        }
      } catch (err) {
        console.error("Failed to fetch companies list", err);
      }
    }
  };

  // France Vendor
  const fetchPeppolConfig = async () => {
    if (
      config.complianceStandardKey === "FrancePA" ||
      config.complianceStandardKey === "france"
    ) {
      try {
        const res = await GetAsync(
          `${API_GATEWAY}/api/invoicecomplianceservice/PeppolInboxConfiguration/list?ownerType=0`,
        );
        if (res.status === 200 && res.data) {
          setPeppolOptions(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch Vendor Peppol Configurations", err);
      }
    }
  };

  const fetchManualDocumentJSON = async (
    profileValues: Record<string, any>,
  ) => {
    const res = await getManualDocumentJson(draftDocId);
    if (res.status !== 200) {
      enqueueSnackbar(res.data.Detail, { variant: "error" });
      reset(profileValues);
      handleFetchDocumentNumber();
      handleFetchTaxRates();
      return;
    }

    const data = JSON.parse(res.data);
    const mapper = config.mapDocumentFetch ?? defaultDocumentMapper;
    const mapped = mapper(data, { isDuplicate, taxRates });
    setFileName(data.documentName);
    reset({
      ...profileValues,
      ...mapped,
      manualDocumentId: draftDocId,
      referenceDocumentId: data.ReferenceDocumentId,
    });
    handleFetchDocumentNumber();
    handleFetchTaxRates();
  };

  const handleReset = (fields: Record<string, any> = {}) => {
    reset({ ...defaults, ...fields });
    setFileName("");
  };

  const handleRefetchDocumentNumber = () => {
    handleFetchDocumentNumber();
    setIsDocSeriesDialogVisible(false);
    enqueueSnackbar(translate("document_number_regenerated_successfully"), {
      variant: "success",
    });
  };

  const submit = async (
    values: any,
    isCreateNew: boolean,
    isCreateDraft?: boolean,
  ) => {
    const setFlag = isCreateDraft
      ? setIsSubmittingDraft
      : isCreateNew
      ? setIsSubmittingNew
      : setIsSubmitting;
    setFlag(true);

    const { isSaveAsDraft, ...payloadData } = values;

    if (actualCompanyId) {
      payloadData.companyId = actualCompanyId;
    }

    if (payloadData.items && payloadData.items.length > 0) {
      payloadData.items = payloadData.items.map((item: any) => {
        if (item.dtoType === "percentage" && item.dto > 0) {
          const gross = (item.unitPrice || 0) * (item.quantity || 0);
          item.dto = Number(((item.dto / 100) * gross).toFixed(2));
        }
        delete item.dtoType;
        return item;
      });
    }
    if (
      payloadData.discountType === "percentage" &&
      payloadData.totalDiscount > 0
    ) {
      const subtotal = (payloadData.items || []).reduce(
        (sum: number, item: any) => {
          const gross = (item.unitPrice || 0) * (item.quantity || 0);
          const itemDto = item.dto || 0;
          return sum + Math.max(0, gross - itemDto);
        },
        0,
      );

      payloadData.totalDiscount = Number(
        ((payloadData.totalDiscount / 100) * subtotal).toFixed(2),
      );
    }

    delete payloadData.discountType;

    const endpointSource = isCreateNew
      ? config.createNewEndpoint ?? config.createEndpoint
      : config.createEndpoint;

    const endpoint = resolveEndpoint(endpointSource, payloadData);

    try {
      const formData = buildPayload({
        values: { ...payloadData, isSaveAsDraft },
        fileName,
        pdfBlobRef,
        config,
      });
      const res = await PostFileAsync(
        `${API_GATEWAY}/api/manualdocumentservice/ManualDocument${endpoint}`,
        formData,
      );

      if (res.status === 200) {
        enqueueSnackbar(
          translate(
            isSaveAsDraft
              ? "document_draft_saved_successfully"
              : "document_created_successfully",
          ),
          {
            variant: "success",
          },
        );
        if (isCreateNew) {
          if (draftDocId) {
            reset();
            const route = getCreateDocumentRoute({
              taxRatesChannel: config.taxRatesChannel,
              invoiceType: config.invoiceType,
              documentClass: config.documentClass,
            });
            console.log("BEFORE NAVIGATE");
            navigate(route, { replace: true });
            console.log("AFTER NAVIGATE");
          } else {
            const preserve: Record<string, any> = {};
            (config.preserveOnReset ?? []).forEach((k) => {
              preserve[k] = values[k];
            });
            handleReset({
              documentClass: config.documentClass,
              accountClass: config.accountClass,
              documentDate: new Date(),
              ...preserve,
            });
            setFormResetSignal((s) => s + 1);
            handleFetchDocumentNumber();
            handleFetchTaxRates();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        } else {
          reset();

          if (draftDocId && !isSaveAsDraft) {
            handleRedirectToDocumentScreen(false);
          } else if (!draftDocId && isSaveAsDraft) {
            handleRedirectToDocumentScreen(true);
          } else {
            navigate(-1);
          }
        }
      } else {
        handleDocumentCreateError({
          res,
          enqueueSnackbar,
          translate,
          handleResolveDuplicateDoc,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar(translate("something_went_wrong"), { variant: "error" });
    } finally {
      setFlag(false);
    }
  };

  const createDocument = (values: any) => submit(values, false);

  const createNewDocument = (values: any) => submit(values, true);

  const createDraftDocument = (values: any) =>
    submit({ ...values, isSaveAsDraft: true }, false, true);

  const handleResolveDuplicateDoc = (docNum: string) => {
    setDuplicateDocumentNumber(docNum);
    setIsDocSeriesDialogVisible(true);
  };

  const init = async () => {
    setIsInitLoading(true);
    try {
      const profileValues = await fetchUserProfile();
      await fetchPeppolConfig();
      await fetchCompaniesList();

      if (fileId) {
        await fetchDocument(profileValues);
      } else if (draftDocId) {
        await fetchManualDocumentJSON(profileValues);
      } else {
        reset(profileValues);
        await handleFetchDocumentNumber();
        await handleFetchTaxRates();
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(translate(e), { variant: "error" });
    } finally {
      setIsInitLoading(false);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshPage > 0) {
      const timer = setTimeout(() => handleFetchDocumentNumber(), 1500);
      return () => clearTimeout(timer);
    }
  }, [refreshPage]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.returnValue = message; // For modern browsers
        return message; // For some older browsers
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, navigate]);

  // France Customer
  const fetchCustomerPeppolConfig = async () => {
    if (
      (config.complianceStandardKey === "FrancePA" ||
        config.complianceStandardKey === "france") &&
      customerId
    ) {
      try {
        const res = await GetAsync(
          `${API_GATEWAY}/api/invoicecomplianceservice/PeppolInboxConfiguration/list?ownerType=1&ownerId=${customerId}`,
        );
        if (res.status === 200 && res.data) {
          setCustomerPeppolOptions(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch Customer Peppol Configurations", err);
        setCustomerPeppolOptions([]);
      }
    } else {
      setCustomerPeppolOptions([]);
    }
  };

  useEffect(() => {
    fetchCustomerPeppolConfig();
  }, [customerId, config.complianceStandardKey]);

  useEffect(() => {
    let ignore = false;
    const fetchCompanyLegalRep = async () => {
      if (!requiresLegalValidation) {
        setCompanyLegalRep(null);
        setLegalRepStatus("idle");
        return;
      }

      if (requiresLegalValidation && actualCompanyId) {
        setLegalRepStatus("loading");
        try {
          const res = await GetAsync(
            `${API_GATEWAY}/api/invoicecomplianceservice/ComplianceLegalRepresentative/company/${actualCompanyId}`,
          );

          if (!ignore) {
            if (
              res.status === 200 &&
              res.data &&
              res.data.isEligibleForSubmission === true
            ) {
              setCompanyLegalRep(res.data);
              setLegalRepStatus("valid");
            } else {
              setCompanyLegalRep(null);
              setLegalRepStatus("invalid");
            }
          }
        } catch (err) {
          if (!ignore) {
            console.error("Failed to fetch Company Legal Representative", err);
            setCompanyLegalRep(null);
            setLegalRepStatus("invalid");
          }
        }
      } else {
        setCompanyLegalRep(null);
        setLegalRepStatus("idle");
      }
    };

    fetchCompanyLegalRep();
    return () => {
      ignore = true;
    };
  }, [actualCompanyId, requiresLegalValidation]);

  return {
    config,
    methods,
    handleSubmit,
    watch,
    isSubmitting,
    isSubmittingNew,
    isSubmittingDraft,
    createDocument,
    createNewDocument,
    createDraftDocument,
    items,
    taxes,
    taxRates,
    handleFetchTaxRates,
    fileName,
    setFileName,
    pdfBlobRef,
    validationError,
    docNumberError,
    isLegalRepValid,
    isLoading: isInitLoading,
    isDocSeriesDialogVisible,
    setIsDocSeriesDialogVisible,
    duplicateDocumentNumber,
    handleRefetchDocumentNumber,
    showDialogLeavingPage,
    confirmNavigation,
    cancelNavigation,
    isFormReset: formResetSignal,
    peppolOptions,
    customerPeppolOptions,
    actualCompanyId,
  };
};
